import { doc, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from './config';

/**
 * Utility functions for managing admin users in the new security model
 * where admin status is determined by existence of a document in the adminUsers collection
 */

/**
 * Check if a user has admin privileges
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>} - True if user is an admin
 */
export const checkAdminStatus = async (userId) => {
  if (!userId) return false;

  try {
    const adminDocRef = doc(db, 'adminUsers', userId);
    const docSnap = await getDoc(adminDocRef);
    return docSnap.exists();
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Grant admin privileges to a user
 * @param {string} userId - The user ID to make admin
 * @param {string} grantedBy - The user ID of the admin granting this privilege
 * @returns {Promise<boolean>} - True if successful
 */
export const grantAdminPrivilege = async (userId, grantedBy) => {
  if (!userId) return false;

  try {
    const adminDocRef = doc(db, 'adminUsers', userId);
    await setDoc(adminDocRef, {
      grantedBy,
      grantedAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error('Error granting admin privilege:', error);
    return false;
  }
};

/**
 * Revoke admin privileges from a user
 * @param {string} userId - The user ID to remove admin privilege from
 * @param {string} revokedBy - The user ID of the admin revoking this privilege
 * @returns {Promise<boolean>} - True if successful
 */
export const revokeAdminPrivilege = async (userId, revokedBy) => {
  if (!userId) return false;

  try {
    const adminDocRef = doc(db, 'adminUsers', userId);
    // You may want to log who revoked this privilege by adding an entry to an audit log
    await deleteDoc(adminDocRef);
    return true;
  } catch (error) {
    console.error('Error revoking admin privilege:', error);
    return false;
  }
};

/**
 * Create initial admin user - should only be used during initial setup
 * @param {string} userId - The user ID to make the first admin
 * @returns {Promise<boolean>} - True if successful
 */
export const createInitialAdmin = async (userId) => {
  if (!userId) return false;

  try {
    const adminDocRef = doc(db, 'adminUsers', userId);
    await setDoc(adminDocRef, {
      isInitialAdmin: true,
      grantedAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error('Error creating initial admin:', error);
    return false;
  }
};
